import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Alert, Button, Input, Field, Loader, Modal, Table } from '@itds/elements/index.js';
import { Colors, FontSizes } from '@itds/Theme';

import { Api } from '../../../../utils/Api';

const ContributionsList = ({ version }) => {
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedContributions, setSelectedContributions] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [editingContribution, setEditingContribution] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    minimum_initial_qualified_contribution: '',
    minimum_initial_non_qualified_contribution: '',
    minimum_additional_contribution_qualified: '',
    minimum_additional_contribution_non_qualified: '',
    maximum_initial_contribution: '',
    maximum_initial_contribution_non_qualified: '',
    maximum_additional_contribution: '',
    maximum_additional_contribution_non_qulaified: ''
  });

  useEffect(() => {
    if (version?.id) {
      loadContributions();
    }
  }, [version]);

  const loadContributions = async action => {
    try {
      setLoading(true);

      setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 3000);

      const response = await Api.getContributions(version.id);
      const contributionData = response.data || [];

      setContributions(contributionData);

      if (action === 'add') {
        setSuccessMessage('Contribution Successfully Added');
      } else if (action === 'update') {
        setSuccessMessage('Contribution Successfully Updated');
      } else if (action === 'delete') {
        setSuccessMessage('Contribution(s) Successfully Deleted');
      }
    } catch (err) {
      console.error('Error loading contributions:', err);
      if (action === 'add') {
        setErrorMessage('Unable to add Contribution at this time');
      } else if (action === 'update') {
        setErrorMessage('Unable to update Contribution at this time');
      } else if (action === 'delete') {
        setErrorMessage('Unable to delete Contribution(s) at this time');
      } else {
        setErrorMessage('Failed to load contributions');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      minimum_initial_qualified_contribution: '',
      minimum_additional_contribution_qualified: '',
      minimum_initial_non_qualified_contribution: '',
      minimum_additional_contribution_non_qualified: '',
      maximum_initial_contribution: '',
      maximum_additional_contribution: '',
      maximum_initial_contribution_non_qualified: '',
      maximum_additional_contribution_non_qulaified: ''
    });
  };

  const handleSelectAll = e => {
    e.stopPropagation();

    if (contributions.length === selectedContributions.length) {
      setSelectedContributions([]);
    } else {
      setSelectedContributions(contributions.map(c => c.id));
    }
  };

  const handleContributionSelectChange = (contribution, e) => {
    e.stopPropagation();

    const selectedIds = selectedContributions.filter(id => id !== contribution.id);

    if (!selectedContributions.includes(contribution.id)) {
      selectedIds.push(contribution.id);
    }

    setSelectedContributions(selectedIds);
  };

  const handleCreate = () => {
    resetForm();
    setEditingContribution(null);
    setShowCreateModal(true);
  };

  const handleEdit = (contribution, e) => {
    if (e) e.stopPropagation();

    setEditingContribution(contribution);
    setFormData({
      minimum_initial_qualified_contribution:
        contribution.minimum_initial_qualified_contribution || '',
      maximum_initial_contribution: contribution.maximum_initial_contribution || '',
      minimum_additional_contribution_qualified:
        contribution.minimum_additional_contribution_qualified || '',
      maximum_additional_contribution: contribution.maximum_additional_qualified_contribution || '',
      minimum_initial_non_qualified_contribution:
        contribution.minimum_initial_non_qualified_contribution || '',
      maximum_initial_contribution_non_qualified:
        contribution.maximum_initial_contribution_non_qualified || '',
      minimum_additional_contribution_non_qualified:
        contribution.minimum_additional_contribution_non_qualified || '',
      maximum_additional_contribution_non_qulaified:
        contribution.maximum_additional_contribution_non_qulaified || ''
    });
    setShowEditModal(true);
  };

  const handleDelete = (contribution, e) => {
    e.stopPropagation();
    setSelectedContributions([contribution.id]);
    setShowBulkDelete(true);
  };

  const handleBulkDelete = async () => {
    try {
      setLoading(true);
      setShowBulkDelete(false);

      await Promise.all(selectedContributions.map(id => Api.deleteContribution(id)));

      setSelectedContributions([]);
      loadContributions('delete');
    } catch (err) {
      console.error('Error deleting contributions:', err);
      setErrorMessage('Failed to delete contribution(s)');
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = {
        product_id: parseInt(version.id, 10),
        beacon_id: version.beacon_id || 0,
        version_id: version.version_id || 0,
        product_type: version.product_type || '',
        product_type_detail: version.product_type_detail || '',
        ...formData
      };

      // Convert string values to numbers where applicable
      Object.keys(payload).forEach(key => {
        if (key.includes('contribution') && payload[key] !== '') {
          payload[key] = parseFloat(payload[key]) || null;
        }
      });

      let response;

      if (editingContribution) {
        response = await Api.updateContribution(editingContribution.id, payload);
        loadContributions('update');
      } else {
        response = await Api.postContribution(payload);
        loadContributions('add');
      }

      if (response) {
        setShowCreateModal(false);
        setShowEditModal(false);
        setEditingContribution(null);
        resetForm();
      }
    } catch (err) {
      console.error('Error saving contribution:', err);
      setErrorMessage(`Failed to ${editingContribution ? 'update' : 'create'} contribution`);
    } finally {
      setSaving(false);
    }
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingContribution(null);
    resetForm();
  };

  const handleRowClick = contribution => {
    handleEdit(contribution);
  };

  const formatCurrency = value => {
    if (value === null || typeof value === 'undefined') return '-';
    return `$${parseFloat(value).toLocaleString()}`;
  };

  const renderModal = () => {
    const isEdit = !!editingContribution;
    const isOpen = showCreateModal || showEditModal;
    const title = isEdit ? 'Edit Contribution' : 'Create New Contribution';

    if (!isOpen) return null;

    return (
      <Modal
        buttons={[
          {
            onClick: handleModalClose,
            children: 'Cancel',
            style: { marginRight: '10px' }
          },
          {
            onClick: handleSave,
            children: (() => {
              if (saving) return 'Saving...';
              return isEdit ? 'Update' : 'Create';
            })(),
            disabled: saving,
            color: 'BLUE'
          }
        ]}
        maxWidth={600}
        onClose={handleModalClose}
        title={title}
      >
        <div style={{ padding: '20px', minWidth: '600px' }}>
          {/* Qualified Contributions Section */}
          <div style={{ marginBottom: '30px' }}>
            <h4
              style={{
                marginBottom: '15px',
                color: '#333',
                borderBottom: '1px solid #eee',
                paddingBottom: '10px'
              }}
            >
              Qualified Contributions
            </h4>

            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
              <div style={{ flex: 1 }}>
                <Field label='Minimum Initial' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('minimum_initial_qualified_contribution', e.target.value)
                    }
                    placeholder='Enter amount'
                    type='number'
                    value={formData.minimum_initial_qualified_contribution}
                  />
                </Field>
              </div>

              <div style={{ flex: 1 }}>
                <Field label='Maximum Initial' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('maximum_initial_contribution', e.target.value)
                    }
                    placeholder='Enter amount'
                    type='number'
                    value={formData.maximum_initial_contribution}
                  />
                </Field>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px' }}>
              <div style={{ flex: 1 }}>
                <Field label='Minimum Additional' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('minimum_additional_contribution_qualified', e.target.value)
                    }
                    placeholder='Enter amount'
                    type='number'
                    value={formData.minimum_additional_contribution_qualified}
                  />
                </Field>
              </div>

              <div style={{ flex: 1 }}>
                <Field label='Maximum Additional' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange('maximum_additional_contribution', e.target.value)
                    }
                    placeholder='Enter amount'
                    type='number'
                    value={formData.maximum_additional_contribution}
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* Non-Qualified Contributions Section */}
          <div>
            <h4
              style={{
                marginBottom: '15px',
                color: '#333',
                borderBottom: '1px solid #eee',
                paddingBottom: '10px'
              }}
            >
              Non-Qualified Contributions
            </h4>

            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
              <div style={{ flex: 1 }}>
                <Field label='Minimum Initial' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange(
                        'minimum_initial_non_qualified_contribution',
                        e.target.value
                      )
                    }
                    placeholder='Enter amount'
                    type='number'
                    value={formData.minimum_initial_non_qualified_contribution}
                  />
                </Field>
              </div>

              <div style={{ flex: 1 }}>
                <Field label='Maximum Initial' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange(
                        'maximum_initial_contribution_non_qualified',
                        e.target.value
                      )
                    }
                    placeholder='Enter amount'
                    type='number'
                    value={formData.maximum_initial_contribution_non_qualified}
                  />
                </Field>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px' }}>
              <div style={{ flex: 1 }}>
                <Field label='Minimum Additional' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange(
                        'minimum_additional_contribution_non_qualified',
                        e.target.value
                      )
                    }
                    placeholder='Enter amount'
                    type='number'
                    value={formData.minimum_additional_contribution_non_qualified}
                  />
                </Field>
              </div>

              <div style={{ flex: 1 }}>
                <Field label='Maximum Additional' style={{}}>
                  <Input
                    onChange={e =>
                      handleInputChange(
                        'maximum_additional_contribution_non_qulaified',
                        e.target.value
                      )
                    }
                    placeholder='Enter amount'
                    type='number'
                    value={formData.maximum_additional_contribution_non_qulaified}
                  />
                </Field>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      {successMessage && <Alert type='success'>{successMessage}</Alert>}
      {errorMessage && <Alert type='error'>{errorMessage}</Alert>}

      {renderModal()}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDelete && (
        <Modal
          buttons={[
            {
              color: 'RED',
              onClick: handleBulkDelete,
              children: 'Delete',
              style: { marginLeft: 'auto' }
            }
          ]}
          onClose={() => setShowBulkDelete(false)}
          title='Are you sure?'
        >
          <div
            style={{
              padding: 30,
              lineHeight: '30px',
              fontSize: FontSizes.LARGE,
              textAlign: 'center'
            }}
          >
            Be careful...you are about to delete {selectedContributions.length} contribution
            record(s). This will remove them completely from the platform. Make sure you know what
            you are doing before completing this action.
          </div>
        </Modal>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', marginBottom: 30, justifyContent: 'flex-end' }}>
        <Button
          color='RED'
          disabled={!selectedContributions.length}
          inline={true}
          onClick={() => setShowBulkDelete(true)}
          style={{ marginRight: 10 }}
        >
          Delete Selected
        </Button>
        <Button inline={true} onClick={handleCreate} style={{}}>
          Create New Contribution
        </Button>
      </div>

      {/* Contributions Table */}
      <div
        style={{
          fontSize: '14px',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          overflow: 'hidden'
        }}
      >
        <style>
          {`
            .contributions-table th,
            .contributions-table td {
              border-right: 1px solid #e0e0e0;
              padding: 8px 12px;
            }
            .contributions-table th:last-child,
            .contributions-table td:last-child {
              border-right: none;
            }
            .contributions-table tbody tr {
              border-bottom: 1px solid #f0f0f0;
            }
            .contributions-table tbody tr:hover {
              background-color: #f8f9fa;
            }
            .contributions-table thead th {
              background-color: #f5f5f5;
              border-bottom: 2px solid #d0d0d0;
              font-weight: 600;
            }
          `}
        </style>
        <div className='contributions-table'>
          <Table
            columns={[
              {
                id: 'select',
                label: (
                  <i
                    className={`mdi ${
                      contributions.length === selectedContributions.length
                        ? 'mdi-checkbox-marked'
                        : 'mdi-checkbox-blank-outline'
                    }`}
                    onClick={handleSelectAll}
                    style={{
                      fontSize: 16,
                      cursor: 'pointer',
                      color:
                        contributions.length === selectedContributions.length
                          ? Colors.GREEN.hex
                          : '#777'
                    }}
                  />
                ),
                render: contribution => {
                  const selected = selectedContributions.includes(contribution.id);

                  return (
                    <i
                      className={`mdi ${
                        selected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'
                      }`}
                      onClick={handleContributionSelectChange.bind(null, contribution)}
                      style={{
                        fontSize: 16,
                        cursor: 'pointer',
                        color: selected ? Colors.GREEN.hex : '#777'
                      }}
                    />
                  );
                },
                styles: { width: '30px', minWidth: '30px' }
              },
              {
                id: 'min_initial_qualified',
                label: <span title='Minimum Initial Qualified Contribution'>Min-Init-Qual</span>,
                is_sortable: true,
                render: contribution =>
                  formatCurrency(contribution.minimum_initial_qualified_contribution),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'max_initial_qualified',
                label: <span title='Maximum Initial Qualified Contribution'>Max-Init-Qual</span>,
                is_sortable: true,
                render: contribution => formatCurrency(contribution.maximum_initial_contribution),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'min_additional_qualified',
                label: <span title='Minimum Additional Qualified Contribution'>Min-Add-Qual</span>,
                is_sortable: true,
                render: contribution =>
                  formatCurrency(contribution.minimum_additional_contribution_qualified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'max_additional_qualified',
                label: <span title='Maximum Additional Qualified Contribution'>Max-Add-Qual</span>,
                is_sortable: true,
                render: contribution =>
                  formatCurrency(contribution.maximum_additional_contribution),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'min_initial_non_qualified',
                label: (
                  <span title='Minimum Initial Non-Qualified Contribution'>Min-Init-Non-Q</span>
                ),
                is_sortable: true,
                render: contribution =>
                  formatCurrency(contribution.minimum_initial_non_qualified_contribution),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'max_initial_non_qualified',
                label: (
                  <span title='Maximum Initial Non-Qualified Contribution'>Max-Init-Non-Q</span>
                ),
                is_sortable: true,
                render: contribution =>
                  formatCurrency(contribution.maximum_initial_contribution_non_qualified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'min_additional_non_qualified',
                label: (
                  <span title='Minimum Additional Non-Qualified Contribution'>Min-Add-Non-Q</span>
                ),
                is_sortable: true,
                render: contribution =>
                  formatCurrency(contribution.minimum_additional_contribution_non_qualified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'max_additional_non_qualified',
                label: (
                  <span title='Maximum Additional Non-Qualified Contribution'>Max-Add-Non-Q</span>
                ),
                is_sortable: true,
                render: contribution =>
                  formatCurrency(contribution.maximum_additional_contribution_non_qulaified),
                styles: { fontSize: '15px', whiteSpace: 'nowrap' }
              },
              {
                id: 'updated_at',
                label: 'Updated',
                is_sortable: true,
                render: contribution => format(new Date(contribution.updated_at), 'MM/dd/yy'),
                styles: { fontSize: '15px', whiteSpace: 'nowrap', width: '70px' }
              },
              {
                id: 'actions',
                label: '',
                render: contribution => {
                  return (
                    <div style={{ display: 'flex', gap: '2px' }}>
                      {/* <Button
                      inline={true}
                      onClick={handleEdit.bind(null, contribution)}
                      small={true}
                      style={{ fontSize: '10px', padding: '2px 6px' }}
                    >
                      Edit
                    </Button> */}
                      <Button
                        color='RED'
                        inline={true}
                        onClick={handleDelete.bind(null, contribution)}
                        small={true}
                        style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          display: 'inline-flex',
                          justifyContent: 'center'
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  );
                },
                styles: { textAlign: 'right', width: '80px', minWidth: '80px' }
              }
            ]}
            data={contributions}
            defaultSortColumn={{
              id: 'updated_at',
              label: 'Updated',
              is_sortable: true
            }}
            onRowClick={handleRowClick}
            perPage={20}
            placeholder='No contributions found'
          />
        </div>
      </div>
    </>
  );
};

ContributionsList.propTypes = {
  version: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    beacon_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    version_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    product_type: PropTypes.string,
    product_type_detail: PropTypes.string
  })
};

export default ContributionsList;
